import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useSeriesSuggestions() {
  const queryClient = useQueryClient();

  const { data: suggestions, isLoading } = useQuery({
    queryKey: ['series-suggestions'],
    queryFn: async () => {
      const { data: suggestionsData, error: suggestionsError } = await supabase
        .from('series_suggestions')
        .select(`
          *,
          series_books (
            id,
            position_in_series,
            is_found,
            temp_title,
            temp_isbn13,
            book:books (
              id,
              title,
              cover_url,
              isbn13,
              isbn10
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (suggestionsError) throw suggestionsError;

      // Transform the data to match our UI structure
      return suggestionsData?.map(suggestion => ({
        id: suggestion.id,
        seriesName: suggestion.series_name,
        authorName: suggestion.author_name,
        confidence: Math.round((suggestion.confidence_score || 0.5) * 10), // Convert 0-10 scale to percentage
        source: suggestion.source,
        booksFound: suggestion.series_books?.filter(b => b.is_found).length || 0,
        expectedBooks: suggestion.total_books_expected || 0,
        status: suggestion.status,
        // Count all books (both linked and temp)
        totalBooks: suggestion.series_books?.length || 0,
        books: suggestion.series_books
          ?.filter(b => b.is_found && b.book)
          .map(sb => ({
            id: sb.book.id,
            title: sb.book.title,
            position: sb.position_in_series,
            isbn: sb.book.isbn13 || sb.book.isbn10 || 'Unknown',
            coverUrl: sb.book.cover_url || '/placeholder.svg',
          }))
          .sort((a, b) => a.position - b.position) || [],
        gaps: Array.from(
          { length: suggestion.total_books_expected || 0 },
          (_, i) => i + 1
        ).filter(
          pos => !suggestion.series_books?.some(sb => sb.position_in_series === pos && sb.is_found)
        ),
        // Include temp book data for display purposes
        tempBooks: suggestion.series_books
          ?.filter(b => !b.is_found && (b.temp_title || b.temp_isbn13))
          .map(sb => ({
            position: sb.position_in_series,
            title: sb.temp_title,
            isbn: sb.temp_isbn13,
          })) || [],
      })) || [];
    },
  });

  const approveSuggestion = useMutation({
    mutationFn: async (suggestionId: string) => {
      const { error } = await supabase
        .from('series_suggestions')
        .update({ status: 'approved', reviewed_at: new Date().toISOString() })
        .eq('id', suggestionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series-suggestions'] });
      toast.success('Series approved successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to approve: ${error.message}`);
    },
  });

  const rejectSuggestion = useMutation({
    mutationFn: async (suggestionId: string) => {
      const { error } = await supabase
        .from('series_suggestions')
        .update({ status: 'rejected', reviewed_at: new Date().toISOString() })
        .eq('id', suggestionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series-suggestions'] });
      toast.success('Series rejected');
    },
    onError: (error: any) => {
      toast.error(`Failed to reject: ${error.message}`);
    },
  });

  const updateSuggestion = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { error } = await supabase
        .from('series_suggestions')
        .update({
          series_name: updates.seriesName,
          series_name_normalized: updates.seriesName.toLowerCase().trim(),
          author_name: updates.authorName,
          author_name_normalized: updates.authorName.toLowerCase().trim(),
          total_books_expected: updates.expectedBooks,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series-suggestions'] });
      toast.success('Series updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  return {
    suggestions: suggestions?.filter(s => s.status === 'pending') || [],
    allSuggestions: suggestions || [],
    isLoading,
    approveSuggestion: approveSuggestion.mutate,
    rejectSuggestion: rejectSuggestion.mutate,
    updateSuggestion: updateSuggestion.mutate,
  };
}